import { lighten, transparentize } from 'polished';

const theme = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: { light: 300, regular: 400, bold: 500 },
  fontSize: {
    xxs: '12px',
    xs: '14px',
    sm: '16px',
    md: '18px',
    lg: '24px',
    xl: '32px',
  },
  spacing: {
    xxs: '4px',
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },
  shadow: {
    xs: '0px 1px 3px 0px rgba(0, 0, 0, 0.05)',
    sm: '0px 4px 8px 0px rgba(0, 0, 0, 0.07)',
    md: '0px 8px 16px 0px rgba(0, 0, 0, 0.17)',
    lg: '0px 8px 24px 0px rgba(0, 0, 0, 0.1)',
  },
  borderRadius: { sm: '4px', md: '8px', lg: '16px', xl: '24px' },
  transition: 'all 0.3s ease',
  ToggleSwitch: {
    trackGap: '3px',
    width: '41px',
    height: '24px',
    borderRadius: '100px',
    trackSize: '18px',
    borderWidth: '0px',
    transition: '100ms cubic-bezier(0, 0, 0.35, 1)',
  },
};
const colors = {
  primary: '#0085FF',
  primaryDark: '#133774',
  primaryHover: '#1F76C2',
  blueDark: '#181B34',
  primaryHighlight: '#DFF0FF',
  mondayHoverLight: '#E6E9EF',
  mondayHoverDark: '#4B4E69',
  private: '#F65F7C',
  shareable: '#A358DF',
  black: '#000000',
  dark: '#1A1D34',
  greyDark: '#252943',
  grey: '#a3aaca',
  greyMiddle: '#2B3056',
  greyLight: '#bbcae8',
  greyCloud: '#C5C7D0',
  greyStorm: '#797E93',

  light: '#d4dff5',
  white: '#FFFFFF',
  blackDim: '#111111',
  blackDimSecondary: '#2C2C2C',
  greyDim: '#AAAAAA',
  greyMediumDim: '#8D8D8D',
  greyDarkDim: '#636363',

  eggYolk: '#FFCB00',
  green: '#00C875',
  greenMiddle: '#027D00',
  greenDark: '#007000',
};

const components = {
  Button: {
    default: {
      borderRadius: theme.borderRadius.sm,
    },
  },
};
const lightThemeMonday = {
  ...theme,
  palette: {
    ...colors,
    bgHoverGrey: colors.mondayHoverLight,
    bgBody: colors.white,
    popUp: colors.white,
    colorBody: colors.dark,
    bgBodyTransparent: transparentize(0.2, colors.white),
    bgCard: colors.white,
    borderCard: colors.grey,
    borderCardHover: colors.primary,
    textContrast1: colors.dark,
    textContrast2: colors.greyDark,
    textContrast3: colors.grey,
    textLink: colors.primary,
    textLinkHover: colors.primaryHover,
    titleCard: colors.dark,
    titleCardHover: colors.primary,
    bgButtonPrimary: colors.primary,
    bgButtonPrimaryHover: colors.primaryHover,
    bgButtonPrimaryActive: colors.primaryHover,
    bgButtonPrimaryDisabled: colors.greyLight,
    colorButtonPrimary: colors.white,
    colorButtonPrimaryDisabled: colors.grey,
    bgButtonSecondary: 'transparent',
    bgButtonSecondaryHover: colors.greyLight,
    bgButtonSecondaryActive: colors.primaryHighlight,
    bgButtonSecondaryDisabled: 'transparent',
    colorButtonSecondary: colors.primary,
    colorButtonSecondaryDisabled: colors.grey,
    bgInput: colors.white,
    colorInput: colors.dark,
    borderInputButton: colors.primary,
    borderInput: colors.grey,
    borderInputFocus: colors.primary,
    borderInputButtonHover: colors.mondayHoverLight,
    bgColumnBoard: colors.greyLight,
    bgColumnBoardDragging: colors.primaryHighlight,
    colorStepbarLine: colors.greyLight,
    colorStepbarLineActive: colors.primary,
    bgCardBoard: colors.white,
    bgCardBoardDragging: colors.light,
    bgCardBoardCombining: colors.primary,
    colorAddButton: colors.greyDark,
    colorAddButtonHover: colors.primary,
    bgCardIdea: colors.white,
    bgCardIdeaDragging: colors.greyLight,
    bgCardIdeaCombining: colors.primary,
    colorCardIdeaIcon: colors.greyDark,
    colorCardIdea: colors.dark,
    colorCardIdeaCombining: colors.white,
    bgActivityInput: colors.white,
    bgActivityInputReadyOnly: colors.light,
    colorActivityInput: colors.dark,
    colorActivityInputDisabled: colors.grey,
    borderActivityInput: colors.greyLight,
    bgDropdown: colors.white,
    bgDropdownItemHover: colors.greyLight,
    badgeNStarted: colors.greyLight,
    badgeInProgress: colors.eggYolk,
    badgeDone: colors.green,
    danger: colors.private,
    colorDropdownItem: colors.dark,
    colorDropdownItemHover: colors.dark,

    switchColor: colors.greyCloud,
    switchCheckedColor: colors.primary,
    switchTrackColor: colors.white,
    switchBorderColor: 'transparent',
  },
  selectStyle: {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? colors.primary : colors.grey,
      ':hover': { borderColor: state.isFocused ? colors.primary : colors.grey },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: 'auto',
      height: 'fit-content',
      paddingLeft: 8,
      borderRadius: 4,
    }),
    dropdownIndicator: (provided) => ({ ...provided, padding: 8 }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: 8,
      marginBottom: 8,
    }),
    clearIndicator: (provided) => ({ ...provided, padding: 8 }),
    multiValue: (provided) => ({ ...provided, borderRadius: 4, margin: 2 }),
    menu: (provided) => ({ ...provided, paddingTop: 4, paddingBottom: 4 }),
    option: (provided) => ({ ...provided, padding: '6px 10px' }),
    multiValueRemove: (provided) => ({ ...provided, padding: '0 2px' }),
  },
  components,
};
const darkThemeMonday = {
  ...theme,
  palette: {
    ...colors,
    bgHoverGrey: colors.mondayHoverDark,
    bgBody: colors.dark,
    popUp: colors.greyMiddle,
    colorBody: colors.light,
    bgBodyTransparent: transparentize(0.2, colors.dark),
    bgCard: lighten(0.1, colors.dark),
    borderCard: colors.dark,
    borderCardHover: colors.greyDark,
    borderInputButton: colors.primary,
    borderInputButtonHover: colors.mondayHoverDark,
    textContrast1: colors.white,
    textContrast2: colors.greyLight,
    textContrast3: colors.grey,
    textLink: lighten(0.2, colors.primary),
    textLinkHover: colors.primaryHighlight,
    titleCard: colors.white,
    titleCardHover: colors.primaryHighlight,
    bgButtonPrimary: colors.primary,
    bgButtonPrimaryHover: colors.primaryHover,
    bgButtonPrimaryActive: colors.primaryHover,
    bgButtonPrimaryDisabled: colors.greyDark,
    colorButtonPrimary: colors.white,
    colorButtonPrimaryDisabled: transparentize(0.5, colors.grey),
    bgButtonSecondary: 'transparent',
    bgButtonSecondaryHover: colors.greyDark,
    bgButtonSecondaryActive: colors.greyDark,
    bgButtonSecondaryDisabled: 'transparent',
    colorButtonSecondary: lighten(0.2, colors.primary),
    colorButtonSecondaryDisabled: colors.greyDark,
    bgInput: colors.dark,
    colorInput: colors.light,
    borderInput: colors.greyDark,
    borderInputFocus: colors.primary,
    bgColumnBoard: lighten(0.1, colors.dark),
    bgColumnBoardDragging: lighten(0.2, colors.dark),
    colorStepbarLine: lighten(0.1, colors.dark),
    colorStepbarLineActive: colors.primary,
    colorAddButton: colors.light,
    colorAddButtonHover: lighten(0.2, colors.primary),
    bgCardIdea: colors.dark,
    bgCardIdeaDragging: lighten(0.1, colors.dark),
    bgCardIdeaCombining: colors.primaryHover,
    colorCardIdea: colors.greyLight,
    colorCardIdeaCombining: colors.white,
    bgActivityInput: colors.dark,
    bgActivityInputReadyOnly: lighten(0.05, colors.dark),
    colorActivityInput: colors.light,
    colorActivityInputDisabled: colors.grey,
    borderActivityInput: colors.greyDark,
    bgDropdown: colors.dark,
    bgDropdownItemHover: lighten(0.05, colors.dark),
    badgeDone: colors.green,
    badgeNStarted: colors.greyLight,
    badgeInProgress: colors.eggYolk,
    colorDropdownItem: colors.greyLight,
    colorDropdownItemHover: colors.grayLight,

    switchColor: colors.greyStorm,
    switchCheckedColor: colors.primary,
    switchTrackColor: colors.blueDark,
    switchBorderColor: 'transparent',
  },
  selectStyle: {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: colors.dark,
      borderColor: state.isFocused ? colors.primary : colors.greyDark,
      ':hover': {
        borderColor: state.isFocused ? colors.primary : colors.greyDark,
      },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: 'auto',
      height: 'fit-content',
      paddingLeft: 8,
      borderRadius: 4,
    }),
    singleValue: (provided) => ({ ...provided, color: colors.light }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: colors.greyDark,
      padding: 8,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: colors.greyDark,
      marginTop: 8,
      marginBottom: 8,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: colors.greyDark,
      padding: 8,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: colors.greyDark,
      borderRadius: 4,
      margin: 2,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: colors.light,
      marginRight: 5,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: colors.dark,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (provided, state) => ({
      ...provided,
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: state.isSelected
        ? colors.primary
        : state.isFocused
        ? transparentize(0.5, colors.greyDark)
        : 'transparent',
      padding: '6px 10px',
    }),
    multiValueRemove: (provided) => ({ ...provided, padding: '0 2px' }),
  },
  components,
};
const blackThemeMonday = {
  ...theme,
  palette: {
    ...colors,
    bgHoverGrey: colors.mondayHoverDark,
    bgBody: colors.blackDim,
    popUp: colors.greyMiddle,
    colorBody: colors.white,
    bgBodyTransparent: transparentize(0.2, colors.greyDarkDim),
    bgCard: colors.blackDimSecondary,
    borderCard: colors.greyDarkDim,
    borderCardHover: colors.greyDarkDim,
    borderInputButton: colors.greyDim,
    borderInputButtonHover: colors.greyDarkDim,

    textContrast1: colors.white,
    textContrast2: colors.greyDim,
    textContrast3: colors.grey,
    textLink: lighten(0.2, colors.primary),
    textLinkHover: colors.primaryHighlight,
    titleCard: colors.white,
    titleCardHover: colors.primaryHighlight,
    bgButtonPrimary: colors.primary,
    bgButtonPrimaryHover: colors.primaryHover,
    bgButtonPrimaryActive: colors.primaryHover,
    bgButtonPrimaryDisabled: colors.greyDarkDim,
    colorButtonPrimary: colors.white,
    colorButtonPrimaryDisabled: colors.greyDim,
    bgButtonSecondary: 'transparent',
    bgButtonSecondaryHover: colors.greyDark,
    bgButtonSecondaryActive: colors.greyDark,
    bgButtonSecondaryDisabled: 'transparent',
    colorButtonSecondary: lighten(0.2, colors.primary),
    colorButtonSecondaryDisabled: colors.greyDark,
    bgInput: colors.blackDim,
    colorInput: colors.white,
    placeholderInputColor: colors.greyDim,
    borderInput: colors.greyMediumDim,
    borderInputFocus: colors.primary,
    bgColumnBoard: lighten(0.1, colors.blackDim),
    bgColumnBoardDragging: lighten(0.2, colors.blackDim),
    colorStepbarLine: colors.greyDarkDim,
    colorStepbarLineActive: colors.primary,
    colorAddButton: colors.greyDim,
    colorAddButtonHover: colors.white,
    bgCardIdea: colors.blackDim,
    bgCardIdeaDragging: lighten(0.1, colors.blackDim),
    bgCardIdeaCombining: colors.primaryHover,
    colorCardIdea: colors.white,
    colorCardIdeaCombining: colors.white,
    bgActivityInput: colors.blackDim,
    bgActivityInputReadyOnly: colors.blackDimSecondary,
    colorActivityInput: colors.white,
    colorActivityInputDisabled: colors.grey,
    borderActivityInput: colors.greyDarkDim,
    badgeDone: colors.green,
    badgeNStarted: colors.greyDarkDim,
    badgeInProgress: colors.eggYolk,
    colorDropdownItem: colors.greyLight,
    colorDropdownItemHover: colors.grayLight,

    switchColor: colors.greyDim,
    switchCheckedColor: colors.primary,
    switchTrackColor: colors.blackDim,
    switchBorderColor: 'transparent',
  },
  selectStyle: {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? colors.primary : colors.greyMediumDim,
      ':hover': {
        borderColor: state.isFocused ? colors.primary : colors.greyMediumDim,
      },
      height: 'fit-content',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: 'auto',
      paddingLeft: 8,
      borderRadius: 4,
      backgroundColor: colors.blackDim,
      color: colors.white,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: colors.greyDim,
      opacity: 1,
    }),
    dropdownIndicator: (provided) => ({ ...provided, padding: 8 }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: 8,
      marginBottom: 8,
      color: colors.blackDimSecondary,
    }),
    clearIndicator: (provided) => ({ ...provided, padding: 8 }),
    multiValue: (provided) => ({
      ...provided,
      borderRadius: 4,
      margin: 2,
      backgroundColor: colors.blackDimSecondary,
      color: colors.white,
    }),
    input: (provided) => ({ ...provided, color: colors.white }),
    multiValueLabel: (provided) => ({ ...provided, color: colors.white }),
    singleValue: (provided) => ({ ...provided, color: colors.white }),
    menu: (provided) => ({
      ...provided,
      marginTop: theme.spacing.xs,
      paddingTop: 4,
      paddingBottom: 4,
      backgroundColor: colors.blackDimSecondary,
    }),
    option: (provided, { isFocused }) => ({
      ...provided,
      padding: '6px 10px',
      color: colors.white,
      backgroundColor: isFocused
        ? colors.greyDarkDim
        : colors.blackDimSecondary,
      ':hover': { backgroundColor: colors.greyDarkDim },
    }),
    multiValueRemove: (provided) => ({ ...provided, padding: '0 2px' }),
  },
  components,
};

export { lightThemeMonday, darkThemeMonday, blackThemeMonday };
